import {AvailabilityInfo, ConfigApi, OrderType} from "../../../Api/types";
import {TFunction} from "i18next";
import {DateHelper, getHourTimeString} from "../../../Common/Date/DateHelper";
import ItemHelper from "./ItemHelper";
import AvailableHelper from "../Availability/AvailableHelper";
import HoursHelper from "../Availability/HoursHelper";
import CategoryHelper from "../Category/CategoryHelper";
import DateService from "../../../Common/Date/DateService";
import {getParsedSessionOrderType} from "../../../../utils/order";

class ItemAvailabilityHelper {
    static getItemOrderTypesAvailabilities = (
        item: Record<string, any>,
        availability: Record<string, any> | undefined,
        category: Record<string, any>,
        menus: Record<string, any>[],
        availabilities: Record<string, any>[],
        pickupDate: string | undefined,
        config: ConfigApi | undefined,
        readonly?: boolean,
    ) => {
        const itemAvailabilities = availability ? [availability] : [];
        const storeOpenHours = config?.hours || [];
        const menusWithCategory = CategoryHelper.getMenusForCategoryByIds(category, menus).filter((menu: Record<string, any>) => item.menuAvailabilitiesIds.includes(menu.availability_id));

        return (Object.keys(OrderType) as OrderType[]).reduce((acc: any, type) => {
            const itemVariants = item.type === "ITEM_GROUP" ? item.items : [item];

            if (!readonly && itemVariants.every((variant: Record<string, any>) => ItemAvailabilityHelper.checkIfItemIsDisabledForOrderType(variant, type))) {
                acc[type] = undefined;
                return acc;
            }

            const menusWithCategoryForOrderType = menusWithCategory.filter((menu: any) => menu[getParsedSessionOrderType(type).toLowerCase()] === true);
            const menuAvailabilitiesForType = AvailableHelper.getAvailabilities(availabilities, menusWithCategoryForOrderType.map((menu: any) => menu.availability_id));

            if (menusWithCategoryForOrderType.length === 0) {
                acc[type] = undefined;
                return acc;
            }

            const anyVariantAlwaysAvailable = itemVariants.some((variant: Record<string, any>) => variant.item_always_available && (readonly ? true : !ItemAvailabilityHelper.checkIfItemIsDisabledForOrderType(variant, type)));
            const variantsWithRestrictions = ItemAvailabilityHelper.getFlattedNotAlwaysAvailableItems(itemVariants).filter(variant => variant.item_nearest_available_date_per_type)
                .sort((a: any, b: any) => a.item_nearest_available_date_per_type[type]?.dateFrom?.getTime() - b.item_nearest_available_date_per_type[type]?.dateFrom?.getTime());
            let nearestAvailableDate = undefined;
            let searchDate = DateService.emptyOrNow(pickupDate);
            const maxSearchTries = 30;
            let currentSearchTry = 0;

            while (!nearestAvailableDate && variantsWithRestrictions.length && !anyVariantAlwaysAvailable) {
                if (currentSearchTry >= maxSearchTries) break;

                for (const variantWithRestriction of variantsWithRestrictions) {
                    const maxSearchDate = new Date(searchDate);
                    maxSearchDate.setDate(maxSearchDate.getDate() + 30);
                    const nextItemAvailableDate = ItemHelper.getNearestAvailabilityDateForItem(variantWithRestriction, itemAvailabilities, searchDate ? DateHelper.format(searchDate) : undefined, type, maxSearchDate);

                    if (nextItemAvailableDate.dateFrom) {
                        AvailableHelper.trimHoursToAvailabilities(menuAvailabilitiesForType, nextItemAvailableDate);
                        HoursHelper.trimHoursToStore(storeOpenHours, nextItemAvailableDate);
                    }

                    if (nextItemAvailableDate.dateTo?.getTime() === nextItemAvailableDate.dateFrom?.getTime()) {
                        searchDate.setDate(searchDate.getDate() + 1);
                        searchDate.setHours(0, 0, 0, 0);
                        currentSearchTry++;
                        continue;
                    }

                    if (nextItemAvailableDate.dateFrom
                        && AvailableHelper.checkIfDateIsInStoreAndMenu(storeOpenHours, itemAvailabilities, nextItemAvailableDate.dateFrom)
                        && searchDate.getTime() <= nextItemAvailableDate.dateFrom.getTime()
                    ) {
                        nearestAvailableDate = nextItemAvailableDate;
                        break;
                    } else if (nextItemAvailableDate.dateTo && nextItemAvailableDate.dateFrom) {
                        const compareDateTo = new Date(nextItemAvailableDate.dateTo);
                        if (compareDateTo <= nextItemAvailableDate.dateFrom) {
                            compareDateTo.setDate(compareDateTo.getDate() + 1);
                        }
                        if (compareDateTo && AvailableHelper.checkIfDateIsInStoreAndMenu(storeOpenHours, menuAvailabilitiesForType.availabilities ?? [], nextItemAvailableDate.dateFrom)
                            && searchDate.getTime() < compareDateTo.getTime()) {
                            nearestAvailableDate = {
                                ...nextItemAvailableDate,
                                dateTo: compareDateTo
                            };
                            break;
                        }
                    }

                    searchDate.setDate(searchDate.getDate() + 1);
                    searchDate.setHours(0, 0, 0, 0);
                    currentSearchTry++;
                }
            }

            if (!nearestAvailableDate) {
                currentSearchTry = 0;
                searchDate = DateService.emptyOrNow(pickupDate);

                while (!nearestAvailableDate && (variantsWithRestrictions.length === 0 || anyVariantAlwaysAvailable)) {
                    if (currentSearchTry >= maxSearchTries) break;

                    for (const availability of menuAvailabilitiesForType) {
                        const availabilityDate = new Date(searchDate);
                        availabilityDate.setHours(0, 0, 0, 0);
                        const date: { dateFrom: Date, dateTo?: Date } = {
                            dateFrom: new Date(availabilityDate),
                            dateTo: undefined,
                        };

                        AvailableHelper.trimHoursToAvailabilities([availability], date);
                        HoursHelper.trimHoursToStore(storeOpenHours, date);

                        if (date.dateTo?.getTime() === date.dateFrom?.getTime()) {
                            searchDate.setDate(searchDate.getDate() + 1);
                            searchDate.setHours(0, 0, 0, 0);
                            currentSearchTry++;
                            continue;
                        }

                        if (date.dateFrom && date.dateTo && AvailableHelper.checkIfDateIsInStoreAndMenu(storeOpenHours, itemAvailabilities, date.dateFrom)
                            && searchDate.getTime() <= date.dateFrom?.getTime()
                        ) {
                            nearestAvailableDate = date;
                            break;
                        } else if (date.dateTo) {
                            const compareDateTo = new Date(date.dateTo);
                            if (compareDateTo <= date.dateFrom) {
                                compareDateTo.setDate(compareDateTo.getDate() + 1);
                            }
                            if (compareDateTo && AvailableHelper.checkIfDateIsInStoreAndMenu(storeOpenHours, itemAvailabilities, date.dateFrom)
                                && searchDate.getTime() < compareDateTo.getTime()) {
                                nearestAvailableDate = {
                                    ...date,
                                    dateTo: compareDateTo
                                };
                                break;
                            }
                        }
                    }

                    searchDate.setDate(searchDate.getDate() + 1);
                    searchDate.setHours(0, 0, 0, 0);
                    currentSearchTry++;
                }
            }

            acc[type] = nearestAvailableDate;

            return acc;
        }, {});
    }

    static getItemAvailabilityInfo = (
        item: Record<string, any>,
        availability: Record<string, any> | undefined,
        config: ConfigApi | undefined,
        menus: Record<string, any>[],
        availabilities: Record<string, any>[],
        category: Record<string, any>,
        pickupDate: string | undefined,
        orderType: OrderType | undefined,
        locale: string | undefined,
        t: TFunction,
        readonly?: boolean
    ): AvailabilityInfo[] => {
        const orderTypesAvailabilities = ItemAvailabilityHelper.getItemOrderTypesAvailabilities(item, availability, category, menus, availabilities, pickupDate, config, readonly);
        const infos: AvailabilityInfo[] = [];

        const typesDateGroups: {
            types: string[],
            dateFrom: Date,
            dateTo: Date,
        }[] = [];
        const typesAvailabilities = Object.keys(orderTypesAvailabilities)
            .map(type => {
                if (!orderTypesAvailabilities[type]) return undefined;

                return orderTypesAvailabilities[type];
            })
            .filter(Boolean)
            .sort((a, b) => a.dateFrom.getTime() - b.dateFrom.getTime());

        if (typesAvailabilities.length) {
            Object.keys(orderTypesAvailabilities)
                .forEach(type => {
                    const otherTypesAvailability = orderTypesAvailabilities[type];
                    if (otherTypesAvailability) {
                        const index = typesDateGroups.findIndex(group => group?.dateFrom.getTime() === otherTypesAvailability?.dateFrom.getTime()
                            && group.dateTo?.getTime() === otherTypesAvailability?.dateTo.getTime())
                        if (index > -1) {
                            typesDateGroups[index].types.push(type);
                        } else {
                            typesDateGroups.push({
                                types: [type],
                                dateFrom: otherTypesAvailability.dateFrom,
                                dateTo: otherTypesAvailability.dateTo,
                            })
                        }
                    }
                });

            typesDateGroups.forEach(group => {
                infos.push({
                    text: `${t("modules.menu.field.available.title")}:`,
                    typesText: readonly ? "" : group.types.map(type => t(`enums.order.type.${type}`)).join(", "),
                    dateText: `${DateHelper.getFormattedNearestDate(DateHelper.format(group.dateFrom), t, locale ?? "", true)} - ${getHourTimeString(group.dateTo)}`,
                    currentType: orderType ? group.types.length === 1 && group.types[0] === orderType : false,
                    isNowAvailable: group.dateFrom && group.dateTo && new Date() >= group.dateFrom && new Date() < group.dateTo,
                    includeCurrentType: orderType ? group.types.includes(orderType) : false,
                });

                const firstIndex = infos.findIndex(i => i.isNowAvailable);
                const nowAvailableInfos = infos.filter((i, index) => i.isNowAvailable && index > firstIndex);
                if (firstIndex > -1) {
                    nowAvailableInfos.forEach(i => {
                        const index = infos.findIndex(info => info.typesText === i.typesText);
                        if (index > -1) {
                            infos[firstIndex].typesText = `${infos[firstIndex].typesText}, ${infos[index].typesText}`;
                            infos.splice(index, 1);
                        }
                    })
                }
            });
        }

        return infos.length > 0 ? infos : [{text: t("common.word.not_available"), isEverAvailable: false}];
    }

    static getCategoryWithNearestAvailableDateForItem = (
        item: Record<string, any>,
        categories: Record<string, any>[],
        menus: Record<string, any>[],
        session: Record<string, any>,
        menuId: number | undefined,
        itemGroup?: Record<string, any>
    ) => {
        const ids = itemGroup ? itemGroup.items.map((itemGroupItem: Record<string, any>) => itemGroupItem.item_id) : [item.id];
        const {menu, categories: menuCategories} = CategoryHelper.findCategoriesForMenu(categories, menus, menuId);
        const finalCategories: Record<string, any>[] = menuId ? [...menuCategories] : [...categories];
        const itemCategories = finalCategories.filter(category => {
            return category.entities.some((entity: Record<string, any>) => ids.includes(entity.entity_id));
        });
        let minDate: Date | undefined = undefined;

        const itemParsedCategories: {
            category: Record<string, any>;
            nearestDate: {
                dateFrom: Date
                dateTo: Date
            } | undefined;
            isAvailable: boolean;
        }[] = [...itemCategories].map((category: Record<string, any>) => {
            const maxSearchDate = DateService.emptyOrNow(session.pickup_at);
            maxSearchDate.setDate(maxSearchDate.getDate() + 30);
            const menusWithCategory = menuId ? (menu ? [menu] : []) : CategoryHelper.getMenusForCategory(menus, category);
            const orderType = session.order_type as OrderType;
            const menusWithCategoryForOrderType = menusWithCategory.filter((menu: any) => menu[getParsedSessionOrderType(orderType).toLowerCase()] === true);
            const menuAvailabilitiesForType = menusWithCategoryForOrderType.map((menu: any) => menu.availability);
            const nearestDate = AvailableHelper.getNearestOpenDateFromNowOrDate(menuAvailabilitiesForType, DateService.emptyOrNow(session.pickup_at), true, maxSearchDate);
            const isCategoryAvailableByHoursForCurrentType = HoursHelper.isOpen(menuAvailabilitiesForType.flatMap((availability: any) => availability.hours), DateService.emptyOrNow(session.pickup_at));
            const isCategoryAvailableByAvailabilityDates = menuAvailabilitiesForType.some((availability: any) => AvailableHelper.isAvailabile(availability, DateService.emptyOrNow(session.pickup_at)));

            return {
                category,
                nearestDate,
                isAvailable: isCategoryAvailableByHoursForCurrentType && isCategoryAvailableByAvailabilityDates && menusWithCategoryForOrderType.length > 0
            }
        }).sort((a, b) => {
            if (a.isAvailable !== b.isAvailable) {
                return a.isAvailable ? -1 : 1;
            }

            const dateA = a.nearestDate?.dateFrom ? new Date(a.nearestDate.dateFrom).getTime() : Infinity;
            const dateB = b.nearestDate?.dateFrom ? new Date(b.nearestDate.dateFrom).getTime() : Infinity;

            return dateA - dateB;
        });

        let minCategory: any | undefined = itemParsedCategories[0]?.category;

        if (itemParsedCategories.length > 1) {
            itemParsedCategories.forEach(categoryObject => {
                const {category} = categoryObject;
                const finalMenus = menuId ? (menu ? [menu] : []) : menus;
                const menusWithCategory = finalMenus.filter((menu: any) => menu.categories.some((c: any) => c.category_id === category.id));
                const menusAvailabilities = menusWithCategory.map((menu: any) => menu.availability);
                const itemDate = ItemHelper.getNearestAvailabilityDateForItem(item, menusAvailabilities, session.pickup_at, session.order_type as OrderType)?.dateFrom;
                if (itemDate && (!minDate || itemDate < minDate)) {
                    minDate = itemDate;
                    minCategory = category;
                }
            });
        }
        return minCategory;
    }

    static getParsedAvailabilityInfos = (availabilityInfos: AvailabilityInfo[]) => {
        const result: string[] = [];

        if (availabilityInfos.length === 1) {
            const info = availabilityInfos[0];

            if ((info.currentType || info.includeCurrentType) && !info.isNowAvailable && info.dateText) {
                result.push(`${info.text} ${info.dateText.toLowerCase()}`);
            } else if (!info.currentType && !info.includeCurrentType) {
                if (info.isNowAvailable) {
                    result.push(`${info.text} ${info.typesText}`);
                } else {
                    if (info.dateText && info.typesText) {
                        result.push(`${info.text} ${info.typesText}. ${info.dateText}`);
                    } else if (info.dateText) {
                        result.push(`${info.text} ${info.dateText.toLowerCase()}`);
                    } else {
                        result.push(info.text);
                    }
                }
            }

            return result;
        }

        if (availabilityInfos.length > 1) {
            availabilityInfos.forEach((info) => {
                let title = `${info.text} ${info.typesText}`;

                if (!info.isNowAvailable && info.dateText) {
                    title += ` - ${info.dateText.toLowerCase()}`;
                }

                result.push(title);
            })
        }

        return result;
    }

    static checkIfItemIsDisabledForOrderType = (item: Record<string, any>, orderType: OrderType) => {
        let allItemOrderTypes: OrderType[] = [];

        if (item.type === "ITEM_GROUP") {
            item.items.forEach((itemItem: Record<string, any>) => {
                if (itemItem.order_types && itemItem.order_types.length > 0) {
                    itemItem.order_types.forEach((orderType: OrderType) => {
                        if (!allItemOrderTypes.includes(orderType)) allItemOrderTypes.push(orderType);
                    })
                }
            });
        } else {
            if (item.order_types && item.order_types.length > 0) {
                allItemOrderTypes = item.order_types.map((orderType: OrderType) => orderType);
            }
        }

        if (allItemOrderTypes.length > 0) {
            return !allItemOrderTypes.includes(orderType);
        }

        return false;
    }

    static isItemAlwaysAvailable = (item: Record<string, any>): boolean => {
        if (item.item_always_available) return true;

        if (item.type === "ITEM_GROUP" && Array.isArray(item.items)) {
            return item.items.some((subItem: Record<string, any>) => ItemAvailabilityHelper.isItemAlwaysAvailable(subItem));
        }

        return false;
    }

    static getFlattedNotAlwaysAvailableItems = (items: Record<string, any>[]) => {
        const result: Record<string, any>[] = [];

        for (const item of items) {
            if (item.type === "ITEM_GROUP" && Array.isArray(item.items)) {
                const subItems = item.items.filter(sub => !ItemAvailabilityHelper.isItemAlwaysAvailable(sub));
                result.push(...subItems);
            } else {
                if (!ItemAvailabilityHelper.isItemAlwaysAvailable(item)) {
                    result.push(item);
                }
            }
        }

        return result;
    }
}

export default ItemAvailabilityHelper;